import * as THREE from 'three';

/**
 * Environment system for skybox, ground, and atmospheric effects
 * Creates a dark fantasy atmosphere inspired by Diablo II / Lineage
 */
export class Environment {
  private scene: THREE.Scene;
  private skyMesh: THREE.Mesh | null = null;
  private stars: THREE.Points | null = null;
  private moon: THREE.Mesh | null = null;
  private groundMesh: THREE.Mesh | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Initialize the complete environment
   */
  public init(): THREE.Mesh {
    this.createSkybox();
    this.createStars();
    this.createMoon();
    return this.createGround();
  }

  /**
   * Create a shader-based gradient skybox
   */
  private createSkybox(): void {
    const vertexShader = `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform vec3 horizonColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;

      void main() {
        float h = normalize(vWorldPosition + offset).y;

        // Create a gradient with horizon glow
        vec3 color;
        if (h > 0.0) {
          // Above horizon - blend to top
          color = mix(horizonColor, topColor, pow(h, exponent));
        } else {
          // Below horizon - darker
          color = mix(horizonColor, bottomColor, pow(-h, 0.5));
        }

        // Add subtle atmospheric scattering near horizon
        float horizonGlow = exp(-abs(h) * 8.0) * 0.15;
        color += vec3(0.2, 0.1, 0.05) * horizonGlow;

        gl_FragColor = vec4(color, 1.0);
      }
    `;

    const uniforms = {
      topColor: { value: new THREE.Color(0x0a0a15) }, // Deep dark blue
      bottomColor: { value: new THREE.Color(0x1a1a2e) }, // Dark purple-blue
      horizonColor: { value: new THREE.Color(0x2a1a1a) }, // Slight red/brown at horizon
      offset: { value: 0 },
      exponent: { value: 0.6 },
    };

    const skyGeo = new THREE.SphereGeometry(800, 32, 32);
    const skyMat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader,
      fragmentShader,
      side: THREE.BackSide,
    });

    this.skyMesh = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(this.skyMesh);
  }

  /**
   * Create procedural star field
   */
  private createStars(): void {
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 2000;
    const positions = new Float32Array(starCount * 3);
    const sizes = new Float32Array(starCount);
    const colors = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
      // Distribute on upper hemisphere only
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI * 0.5; // Only upper half
      const radius = 750 + Math.random() * 50;

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.cos(phi) + 100; // Offset up
      positions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);

      // Vary star sizes
      sizes[i] = 0.5 + Math.random() * 2.0;

      // Slight color variation (warm to cool)
      const colorVar = Math.random();
      if (colorVar > 0.9) {
        // Red giants
        colors[i * 3] = 1.0;
        colors[i * 3 + 1] = 0.7;
        colors[i * 3 + 2] = 0.5;
      } else if (colorVar > 0.8) {
        // Blue stars
        colors[i * 3] = 0.7;
        colors[i * 3 + 1] = 0.8;
        colors[i * 3 + 2] = 1.0;
      } else {
        // White/yellow
        colors[i * 3] = 1.0;
        colors[i * 3 + 1] = 1.0;
        colors[i * 3 + 2] = 0.9;
      }
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starsGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    starsGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const starsMaterial = new THREE.PointsMaterial({
      size: 2,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
    });

    this.stars = new THREE.Points(starsGeometry, starsMaterial);
    this.scene.add(this.stars);
  }

  /**
   * Create an eerie moon
   */
  private createMoon(): void {
    // Moon disc
    const moonGeometry = new THREE.CircleGeometry(30, 32);
    const moonMaterial = new THREE.MeshBasicMaterial({
      color: 0xeeddcc,
      transparent: true,
      opacity: 0.9,
    });

    this.moon = new THREE.Mesh(moonGeometry, moonMaterial);
    this.moon.position.set(-200, 400, -300);
    this.moon.lookAt(0, 0, 0);
    this.scene.add(this.moon);

    // Moon glow
    const glowGeometry = new THREE.CircleGeometry(50, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffeebb,
      transparent: true,
      opacity: 0.15,
      blending: THREE.AdditiveBlending,
    });

    const moonGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    moonGlow.position.copy(this.moon.position);
    moonGlow.lookAt(0, 0, 0);
    this.scene.add(moonGlow);

    // Outer glow
    const outerGlowGeometry = new THREE.CircleGeometry(80, 32);
    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffddaa,
      transparent: true,
      opacity: 0.05,
      blending: THREE.AdditiveBlending,
    });

    const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    outerGlow.position.copy(this.moon.position);
    outerGlow.lookAt(0, 0, 0);
    this.scene.add(outerGlow);
  }

  /**
   * Create improved terrain with vertex colors
   */
  private createGround(): THREE.Mesh {
    const groundSize = 1000;
    const segments = 200;
    const groundGeometry = new THREE.PlaneGeometry(groundSize, groundSize, segments, segments);

    const posAttr = groundGeometry.getAttribute('position');
    const vertexCount = posAttr.count;

    // Create vertex colors for terrain variation
    const colors = new Float32Array(vertexCount * 3);

    for (let i = 0; i < vertexCount; i++) {
      const x = posAttr.getX(i);
      const y = posAttr.getY(i);

      // Create terrain height
      let height = 0;
      height += Math.sin(x * 0.02) * Math.cos(y * 0.02) * 3;
      height += Math.sin(x * 0.05 + 1) * Math.cos(y * 0.03) * 2;
      height += Math.sin(x * 0.01) * Math.sin(y * 0.01) * 5;

      // Add fine noise for texture
      height += (Math.sin(x * 0.3) * Math.cos(y * 0.4) * 0.3);

      // Flatten area near spawn
      const distFromCenter = Math.sqrt(x * x + y * y);
      if (distFromCenter < 80) {
        height *= distFromCenter / 80;
      }

      posAttr.setZ(i, height);

      // Calculate color based on position and height
      const noise1 = Math.sin(x * 0.1) * Math.cos(y * 0.1);
      const noise2 = Math.sin(x * 0.05 + 2) * Math.sin(y * 0.07);

      // Base dark earth color
      let r = 0.15 + noise1 * 0.03;
      let g = 0.13 + noise2 * 0.02;
      let b = 0.08;

      // Add grass patches in certain areas
      const grassNoise = Math.sin(x * 0.02) * Math.cos(y * 0.025) +
        Math.sin(x * 0.08) * Math.cos(y * 0.06) * 0.5;
      if (grassNoise > 0.3 && distFromCenter > 30) {
        // Darker grass tones (dead/dying grass aesthetic)
        r = 0.12 + noise1 * 0.02;
        g = 0.18 + noise2 * 0.04;
        b = 0.08;
      }

      // Rocky areas on higher terrain
      if (height > 3) {
        const rockBlend = Math.min((height - 3) / 3, 1);
        r = r * (1 - rockBlend) + 0.2 * rockBlend;
        g = g * (1 - rockBlend) + 0.18 * rockBlend;
        b = b * (1 - rockBlend) + 0.15 * rockBlend;
      }

      // Lower areas are darker (shadows/mud)
      if (height < -2) {
        const darkBlend = Math.min((-height - 2) / 2, 1) * 0.3;
        r *= (1 - darkBlend);
        g *= (1 - darkBlend);
        b *= (1 - darkBlend);
      }

      // Zone-specific coloring
      // Southwest - corrupted (red tint)
      if (x < -100 && y < -100) {
        const corruptDist = Math.sqrt((x + 180) ** 2 + (y + 180) ** 2);
        if (corruptDist < 100) {
          const blend = 1 - corruptDist / 100;
          r += 0.15 * blend;
          g *= (1 - 0.3 * blend);
          b *= (1 - 0.2 * blend);
        }
      }

      // South - undead area (pale/gray)
      if (y < -80 && x > -50 && x < 50) {
        const undeadDist = Math.abs(y + 150);
        if (undeadDist < 80) {
          const blend = 1 - undeadDist / 80;
          r = r * (1 - blend * 0.3) + 0.12 * blend;
          g = g * (1 - blend * 0.3) + 0.12 * blend;
          b = b * (1 - blend * 0.2) + 0.13 * blend;
        }
      }

      // Northwest - haunted forest (blue-green tint)
      if (x < -80 && y > 80) {
        const hauntedDist = Math.sqrt((x + 150) ** 2 + (y - 150) ** 2);
        if (hauntedDist < 80) {
          const blend = 1 - hauntedDist / 80;
          r *= (1 - 0.2 * blend);
          g += 0.03 * blend;
          b += 0.05 * blend;
        }
      }

      colors[i * 3] = r;
      colors[i * 3 + 1] = g;
      colors[i * 3 + 2] = b;
    }

    groundGeometry.computeVertexNormals();
    groundGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Ground material with vertex colors
    const groundMaterial = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.95,
      metalness: 0.0,
      flatShading: false,
    });

    this.groundMesh = new THREE.Mesh(groundGeometry, groundMaterial);
    this.groundMesh.rotation.x = -Math.PI / 2;
    this.groundMesh.receiveShadow = true;
    this.scene.add(this.groundMesh);

    return this.groundMesh;
  }

  /**
   * Update environment (for animated effects)
   */
  public update(_deltaTime: number): void {
    // Stars could twinkle here if desired
    // Moon could slowly move
  }

  /**
   * Get the ground mesh for raycasting
   */
  public getGroundMesh(): THREE.Mesh | null {
    return this.groundMesh;
  }
}
